import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useObservedElementRef } from "./useObservedElementRef";

/**
 * Keeps a measurement of an element current **before paint**. `measure` runs in a layout effect after
 * every commit, and again whenever a `ResizeObserver` reports a size change React didn't cause (a
 * window resize, a font loading in). A new value is stored only when `isEqual` says it changed, so an
 * unchanged measurement costs no render.
 *
 * Reach for this over {@link useResizeObserver} when a stale frame would show: that hook measures after
 * paint, so whatever renders from the value flashes its unmeasured state on mount and lags one frame
 * behind a resize. Measuring on every commit is cheap as long as `measure` only reads layout: every
 * read in a commit shares one layout pass.
 *
 * When a commit can't change the measurement unless some known input does, pass that input as
 * `commitKey`: the hook then re-measures after a commit only when the key changes, and otherwise
 * only on attach and on observer callbacks. That skips the reads on commits that can't matter, such as
 * the dozens a drag produces.
 *
 * `measure` and `isEqual` are held in refs, so neither needs memoizing.
 *
 * @returns `ref` to attach to the element, and `value`, undefined until the element is first measured
 *
 * @example
 * ```tsx
 * const { ref, value: width } = useLayoutMeasure(
 *   (element) => element.clientWidth,
 *   (a, b) => a === b
 * );
 * return <div ref={ref}>{width !== undefined && <Fitted width={width} />}</div>;
 * ```
 *
 * @example
 * ```tsx
 * // Only the items can change the measured row, so commits that keep them skip the measurement.
 * const { ref, value } = useLayoutMeasure(readRow, sameRow, { commitKey: items.map(({ id }) => id).join() });
 * ```
 */
export interface UseLayoutMeasureOptions {
  /**
   * Re-measure after a commit only when this changes. Omit (or pass `undefined`) to re-measure after
   * every commit; pass a constant to measure only on attach and on observer callbacks.
   */
  commitKey?: unknown;
}

export const useLayoutMeasure = <V, T extends HTMLElement = HTMLDivElement>(
  measure: (element: T) => V,
  isEqual: (previous: V, next: V) => boolean,
  { commitKey }: UseLayoutMeasureOptions = {}
) => {
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;
  const [value, setValue] = useState<V>();
  const { ref, element } = useObservedElementRef<T>();

  const remeasure = useCallback(() => {
    if (!element) return;
    const next = measureRef.current(element);
    setValue((previous) => (previous !== undefined && isEqualRef.current(previous, next) ? previous : next));
  }, [element]);

  const measuredForRef = useRef<{ element: T | null; key: unknown } | null>(null);

  // No dependency array on purpose: without a key, what is measured can change on any commit (new
  // children, new styles), and the equality check makes an unchanged measurement free. With a key,
  // the ref records what was last measured so an unchanged key (on the same element) skips the reads.
  useLayoutEffect(() => {
    if (commitKey !== undefined) {
      const measuredFor = measuredForRef.current;
      if (measuredFor && measuredFor.element === element && Object.is(measuredFor.key, commitKey)) return;
      measuredForRef.current = { element, key: commitKey };
    }
    remeasure();
  });

  useLayoutEffect(() => {
    if (!element) return;
    const observer = new ResizeObserver(remeasure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, remeasure]);

  return { ref, value };
};
